import { useEffect, useState } from "react";
import AuthUser from "./AuthUser";
import toast, { Toaster } from "react-hot-toast";
import moment from "moment";
import TaskSkeleton from "./TaskSkeleton";
import Select, { components } from "react-select";
import { canEdit, isSuperAdmin as isSuperAdminUser } from "../utils/permissions";

const EMPTY = {
  ms_sub_key_id: "",
  company_name: "",
  location: "",
  contact_person: "",
  contact_number: "",
  installed_date: "",
  duration_years: "",
  ending_on: "",
  to_renew: "",
  duration_days: "365",
  qty: "1",
  total_systems: "",
  rate: "",
  status: "Active",
  license_details: "",
  invoice_number: "",
};

const STATUSES = ["Active", "Pending", "Expired", "Inactive"];
const YEAR_OPTIONS = [1, 2, 3, 4, 5];
const MAX_CHILD_KEYS = 5;

const ROW_GROUP_COLORS = [
  "#BFDBFE",
  "#BBF7D0",
  "#FED7AA",
  "#DDD6FE",
  "#FBCFE8",
  "#A5F3FC",
];

const buildGroupColorMap = (groupKeys) => {
  const map = {};
  let i = 0;
  groupKeys.forEach((k) => {
    const key = String(k ?? "");
    if (!(key in map)) {
      map[key] = ROW_GROUP_COLORS[i % ROW_GROUP_COLORS.length];
      i += 1;
    }
  });
  return map;
};

const money = (n) =>
  n === null || n === undefined || n === ""
    ? "—"
    : Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const calcEndDate = (start, years) => {
  if (!start || !years) return "";
  return moment(start).add(Number(years), "years").format("YYYY-MM-DD");
};

const formatDate = (value) => (value ? moment(value).format("YYYY-MM-DD") : "");
const displayDate = (value) => (value ? moment(value).format("DD/MM/YYYY") : "—");
const displayDateTime = (value) =>
  value ? moment(value).format("DD/MM/YYYY") : "—";

/** Remaining days until Ending On (negative = expired). */
const daysDiff = (endDate) => {
  if (!endDate) return null;
  return moment(endDate).startOf("day").diff(moment().startOf("day"), "days");
};

/** Days until master license end (from MS Key activated / end dates). */
const masterDiffDays = (msKey) => {
  if (!msKey) return null;
  if (msKey.activated_end_date) return daysDiff(msKey.activated_end_date);
  if (msKey.activated_date && msKey.duration_years) {
    return moment(msKey.activated_date)
      .startOf("day")
      .add(Number(msKey.duration_years), "years")
      .diff(moment().startOf("day"), "days");
  }
  if (msKey.activated_date) {
    return moment(msKey.activated_date)
      .startOf("day")
      .add(365, "days")
      .diff(moment().startOf("day"), "days");
  }
  return null;
};

const selectStylesSm = {
  control: (base, state) => ({
    ...base,
    minHeight: "31px",
    height: "31px",
    fontSize: "13px",
    borderColor: state.isFocused ? "#0d6efd" : "#ced4da",
    boxShadow: "none",
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px", height: "31px" }),
  indicatorsContainer: (base) => ({ ...base, height: "31px" }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    minWidth: "100%",
    width: "max-content",
    maxWidth: "720px",
  }),
  menuList: (base) => ({ ...base, maxHeight: "320px" }),
  option: (base) => ({ ...base, fontSize: "13px", whiteSpace: "normal" }),
  placeholder: (base) => ({ ...base, fontSize: "13px" }),
  singleValue: (base) => ({ ...base, fontSize: "13px", maxWidth: "100%" }),
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "38px",
    fontSize: "14px",
    borderColor: state.isFocused ? "#0d6efd" : "#ced4da",
    boxShadow: "none",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 10050,
    minWidth: "100%",
    width: "max-content",
    maxWidth: "720px",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 10050 }),
  option: (base) => ({ ...base, fontSize: "13px", padding: "8px 12px", whiteSpace: "normal" }),
  singleValue: (base) => ({ ...base, maxWidth: "100%" }),
};

const QuotaBadges = ({ used, left, max, dark }) => (
  <span className={`quota-opt-badges ${dark ? "dark" : ""}`}>
    <span className="opt-badge used">
      Used {used}/{max}
    </span>
    <span className={`opt-badge ${left === 0 ? "full" : "left"}`}>Left {left}</span>
  </span>
);

const QuotaOption = (props) => (
  <components.Option {...props}>
    <div className="quota-opt-row" title={props.data.label || ""}>
      <span className="quota-opt-label">{props.data.label}</span>
      {props.data.used != null && (
        <QuotaBadges used={props.data.used} left={props.data.left} max={props.data.max} />
      )}
    </div>
  </components.Option>
);

const QuotaSingleValue = (props) => (
  <components.SingleValue {...props}>
    <div className="quota-opt-row compact" title={props.data.label || ""}>
      <span className="quota-opt-label">{props.data.label}</span>
      {props.data.used != null && (
        <QuotaBadges
          used={props.data.used}
          left={props.data.left}
          max={props.data.max}
          dark
        />
      )}
    </div>
  </components.SingleValue>
);

export default function MsChildKeyList() {
  const { http, user } = AuthUser();
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [subKeys, setSubKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterSub, setFilterSub] = useState("");
  const [filterDays, setFilterDays] = useState("");
  const [filterInvoice, setFilterInvoice] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const isSuperAdmin = isSuperAdminUser(user);
  const allowEdit = canEdit(user);
  const rateDetails = (Number(form.rate || 0) * Number(form.total_systems || 0)).toFixed(2);

  useEffect(() => {
    fetchSubKeys();
    fetchRows();
  }, []);

  const fetchSubKeys = () => {
    http.get("/ms_sub_keys").then((res) => {
      if (res.data.success) setSubKeys(res.data.data || []);
    });
  };

  const fetchRows = (subId = "") => {
    setIsLoading(true);
    http
      .get("/ms_child_keys")
      .then((res) => {
        setIsLoading(false);
        if (res.data.success) {
          const all = res.data.data || [];
          setAllRows(all);
          setRows(
            subId ? all.filter((r) => String(r.ms_sub_key_id) === String(subId)) : all
          );
        }
      })
      .catch(() => {
        setIsLoading(false);
        toast.error("Error fetching child keys");
      });
  };

  const subLabel = (s) => {
    const master = s.ms_key?.master_key_id || "Master";
    const share = s.share_name || s.user_name || `Sub #${s.id}`;
    return `${master} → ${share}`;
  };

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY);
    setShowModal(true);
  };

  const datesFromSub = (subId) => {
    const sub = subKeys.find((s) => String(s.id) === String(subId));
    if (!sub) return {};
    const installed_date = formatDate(sub.installed_date);
    const duration_years = sub.duration_years ? String(sub.duration_years) : "";
    const ending_on =
      calcEndDate(installed_date, duration_years) || formatDate(sub.ending_on);
    return {
      duration_years,
      installed_date,
      ending_on,
      to_renew: formatDate(sub.to_renew) || ending_on,
    };
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      ms_sub_key_id: String(row.ms_sub_key_id || ""),
      company_name: row.company_name || "",
      location: row.location || "",
      contact_person: row.contact_person || "",
      contact_number: row.contact_number || "",
      installed_date: formatDate(row.installed_date),
      duration_years: row.duration_years ? String(row.duration_years) : "",
      ending_on: formatDate(row.ending_on),
      to_renew: formatDate(row.to_renew),
      duration_days: row.duration_days != null ? String(row.duration_days) : "",
      qty: row.qty != null ? String(row.qty) : "",
      total_systems: row.total_systems != null ? String(row.total_systems) : "",
      rate: row.rate != null ? String(row.rate) : "",
      status: row.status || "Active",
      license_details: row.license_details || "",
      invoice_number: row.invoice_number || "",
    });
    setShowModal(true);
  };

  const resetModal = () => {
    setShowModal(false);
    setEditing(null);
    setForm(EMPTY);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "ms_sub_key_id" && !editing) {
        Object.assign(next, datesFromSub(value));
      }
      if (name === "installed_date" || name === "duration_years") {
        const end = calcEndDate(
          name === "installed_date" ? value : next.installed_date,
          name === "duration_years" ? value : next.duration_years
        );
        if (end) {
          next.ending_on = end;
          next.to_renew = end;
        }
      }
      if (name === "ending_on") next.to_renew = value;
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allowEdit) {
      toast.error("View-only users cannot modify data");
      return;
    }
    if (!form.ms_sub_key_id) {
      toast.error("Please select a Sub Key");
      return;
    }
    if (!editing) {
      const used = allRows.filter(
        (r) => String(r.ms_sub_key_id) === String(form.ms_sub_key_id)
      ).length;
      if (used >= MAX_CHILD_KEYS) {
        toast.error(`This sub key already has ${MAX_CHILD_KEYS} child keys (max ${MAX_CHILD_KEYS})`);
        return;
      }
    }
    setIsLoading(true);
    try {
      const payload = { ...form, rate_details: rateDetails };
      if (editing) {
        await http.put(`/ms_child_keys/${editing.id}`, payload);
        toast.success("Child key updated");
      } else {
        await http.post("/ms_child_keys", payload);
        toast.success("Child key created");
      }
      resetModal();
      fetchRows(filterSub);
    } catch (error) {
      toast.error(error.response?.data?.message || "Error saving child key");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this child key?")) return;
    try {
      await http.delete(`/ms_child_keys/${id}`);
      toast.success("Child key deleted");
      fetchRows(filterSub);
    } catch (error) {
      toast.error(error.response?.data?.message || "Error deleting child key");
    }
  };

  const filtered = rows
    .filter((row) => {
      const q = searchTerm.toLowerCase();
      if (q) {
        const hit = [
          row.company_name,
          row.location,
          row.contact_person,
          row.contact_number,
          row.license_details,
          row.invoice_number,
          row.status,
          row.sub_key?.share_name,
          row.sub_key?.ms_key?.master_key_id,
        ]
          .join(" ")
          .toLowerCase()
          .includes(q);
        if (!hit) return false;
      }
      if (filterDays !== "") {
        const d = daysDiff(row.ending_on);
        if (d === null) return false;
        if (filterDays === "expired") {
          if (!(d < 0)) return false;
        } else if (!(d < Number(filterDays))) {
          return false;
        }
      }
      if (filterInvoice === "yes") {
        if (!String(row.invoice_number || "").trim()) return false;
      }
      if (filterInvoice === "no") {
        if (String(row.invoice_number || "").trim()) return false;
      }
      if (filterStatus && String(row.status || "") !== filterStatus) {
        return false;
      }
      return true;
    })
    .slice()
    .sort((a, b) => {
      const ma = String(a.sub_key?.ms_key?.master_key_id || "");
      const mb = String(b.sub_key?.ms_key?.master_key_id || "");
      const sa = String(a.sub_key?.share_name || a.ms_sub_key_id || "");
      const sb = String(b.sub_key?.share_name || b.ms_sub_key_id || "");
      return ma.localeCompare(mb) || sa.localeCompare(sb) || Number(a.id) - Number(b.id);
    });

  const renderDaysDiff = (endDate) => {
    const d = daysDiff(endDate);
    if (d === null) return <span className="text-muted">—</span>;
    if (d < 0) {
      return (
        <span className="badge bg-danger" title="Expired">
          {d}
        </span>
      );
    }
    if (d < 30) {
      return (
        <span className="badge bg-warning text-dark" title="Expiring soon">
          {d}
        </span>
      );
    }
    return <span className="fw-bold text-success">{d}</span>;
  };

  const renderMasterDiff = (msKey) => {
    const d = masterDiffDays(msKey);
    if (d === null) return <span className="text-muted">—</span>;
    const title =
      msKey?.activated_date || msKey?.activated_end_date
        ? [
            msKey.activated_date
              ? `Activated ${moment(msKey.activated_date).format("DD/MM/YYYY")}`
              : null,
            msKey.activated_end_date
              ? `End ${moment(msKey.activated_end_date).format("DD/MM/YYYY")}`
              : null,
          ]
            .filter(Boolean)
            .join(" · ")
        : "";
    if (d < 0) {
      return (
        <span className="badge bg-danger" title={title}>
          {d}
        </span>
      );
    }
    if (d < 30) {
      return (
        <span className="badge bg-warning text-dark" title={title}>
          {d}
        </span>
      );
    }
    return (
      <span className="fw-bold text-success" title={title}>
        {d}
      </span>
    );
  };

  const groupColors = buildGroupColorMap(filtered.map((r) => r.ms_sub_key_id));

  const usedChildCount = form.ms_sub_key_id
    ? allRows.filter((r) => String(r.ms_sub_key_id) === String(form.ms_sub_key_id)).length
    : 0;
  const leftChildCount = Math.max(0, MAX_CHILD_KEYS - usedChildCount);

  const buildSubOptions = (includeAll = false) => {
    const options = subKeys
      .map((s) => {
        const used = allRows.filter((r) => String(r.ms_sub_key_id) === String(s.id)).length;
        const left = Math.max(0, MAX_CHILD_KEYS - used);
        return {
          value: String(s.id),
          label: subLabel(s),
          used,
          left,
          max: MAX_CHILD_KEYS,
          isDisabled: !includeAll && left === 0,
          searchText: `${subLabel(s)} used ${used} left ${left}`,
        };
      })
      .sort((a, b) => {
        // Unused (0 used) first, then more Left next, then name
        if (a.used === 0 && b.used !== 0) return -1;
        if (a.used !== 0 && b.used === 0) return 1;
        if (b.left !== a.left) return b.left - a.left;
        return String(a.label).localeCompare(String(b.label));
      });

    if (includeAll) {
      return [{ value: "", label: "All Sub Keys" }, ...options];
    }
    return options;
  };

  const subFilterOptions = buildSubOptions(true);
  const modalSubOptions = buildSubOptions(false);

  const quotaFilterOption = (option, input) => {
    const q = String(input || "").toLowerCase();
    if (!q) return true;
    return (
      String(option.data.label || "").toLowerCase().includes(q) ||
      String(option.data.searchText || "").toLowerCase().includes(q)
    );
  };

  return (
    <div className="container-fluid mt-3 childkey-page">
      <Toaster />
      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white d-flex justify-content-between align-items-center py-3">
          <div>
            <h4 className="mb-0">Child Keys</h4>
            <small className="text-muted">Linked to a Sub Key</small>
          </div>
          {allowEdit && (
          <button className="btn btn-primary" onClick={openAdd}>
            <i className="fas fa-plus-circle me-1"></i> Add Child Key
          </button>
          )}
        </div>
        <div className="card-body">
          <div className="d-flex flex-wrap gap-2 mb-3 align-items-end">
            <div
              className="child-sub-filter"
              style={{ minWidth: "420px", maxWidth: "560px", flex: "1 1 420px" }}
              title={
                (subFilterOptions.find((o) => o.value === String(filterSub)) ||
                  subFilterOptions[0])?.label || ""
              }
            >
              <Select
                options={subFilterOptions}
                value={
                  subFilterOptions.find((o) => o.value === String(filterSub)) ||
                  subFilterOptions[0]
                }
                onChange={(opt) => {
                  const val = opt?.value || "";
                  setFilterSub(val);
                  fetchRows(val);
                }}
                placeholder="Search sub key..."
                isSearchable
                filterOption={quotaFilterOption}
                components={{ Option: QuotaOption, SingleValue: QuotaSingleValue }}
                styles={selectStylesSm}
              />
            </div>
            <input
              type="text"
              className="form-control form-control-sm"
              style={{ maxWidth: "280px" }}
              placeholder="Search company / contact / license..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div style={{ minWidth: "180px" }}>
              <label
                className="form-label mb-1 fw-semibold"
                style={{ fontSize: "12px", color: "#0d6efd" }}
              >
                Days Diff
              </label>
              <select
                className="form-select form-select-sm"
                value={filterDays}
                onChange={(e) => setFilterDays(e.target.value)}
              >
                <option value="">All Days</option>
                <option value="30">Less than 30 Days</option>
                <option value="60">Less than 60 Days</option>
                <option value="90">Less than 90 Days</option>
                <option value="expired">Expired</option>
              </select>
            </div>
            <div style={{ minWidth: "150px" }}>
              <label
                className="form-label mb-1 fw-semibold"
                style={{ fontSize: "12px", color: "#0d6efd" }}
              >
                Invoice
              </label>
              <select
                className="form-select form-select-sm"
                value={filterInvoice}
                onChange={(e) => setFilterInvoice(e.target.value)}
              >
                <option value="">All</option>
                <option value="yes">Yes (has invoice)</option>
                <option value="no">No (no invoice)</option>
              </select>
            </div>
            <div style={{ minWidth: "140px" }}>
              <label
                className="form-label mb-1 fw-semibold"
                style={{ fontSize: "12px", color: "#0d6efd" }}
              >
                Status
              </label>
              <select
                className="form-select form-select-sm"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="">All Status</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => {
                setSearchTerm("");
                setFilterDays("");
                setFilterInvoice("");
                setFilterStatus("");
                setFilterSub("");
                fetchRows("");
              }}
            >
              Clear
            </button>
          </div>

          {isLoading && rows.length === 0 ? (
            <TaskSkeleton />
          ) : (
            <div className="table-responsive child-table-wrap">
              <table className="table table-hover align-middle mb-0 child-table">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Master / Sub</th>
                    <th>Master Activated</th>
                    <th>Master Diff</th>
                    <th>Company Name</th>
                    <th>Location</th>
                    <th>Contact</th>
                    <th>Phone</th>
                    <th>Installed</th>
                    <th>Duration</th>
                    <th>Ending On</th>
                    <th>Days Diff</th>
                    <th>To Renew</th>
                    <th>Qty</th>
                    <th>Systems</th>
                    <th>Rate</th>
                    <th>Rate Details</th>
                    <th>Status</th>
                    <th>Invoice No</th>
                    <th>License</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length > 0 ? (
                    filtered.map((row, i) => (
                      <tr
                        key={row.id}
                        className="group-row"
                        style={{
                          "--row-bg":
                            groupColors[String(row.ms_sub_key_id)] || "#BFDBFE",
                        }}
                      >
                        <td>{i + 1}</td>
                        <td>
                          <div className="fw-semibold trunc-text" style={{ maxWidth: "160px" }} title={row.sub_key?.ms_key?.master_key_id || ""}>
                            {row.sub_key?.ms_key?.master_key_id || "—"}
                          </div>
                          <small className="text-muted trunc-text d-block" style={{ maxWidth: "160px" }} title={row.sub_key?.share_name || row.sub_key?.user_name || ""}>
                            {row.sub_key?.share_name || row.sub_key?.user_name || "—"}
                          </small>
                        </td>
                        <td className="text-nowrap">
                          {displayDateTime(row.sub_key?.ms_key?.activated_date)}
                        </td>
                        <td className="text-center">
                          {renderMasterDiff(row.sub_key?.ms_key)}
                        </td>
                        <td>
                          <div className="trunc-text" style={{ maxWidth: "120px" }} title={row.company_name || ""}>
                            {row.company_name || "—"}
                          </div>
                        </td>
                        <td>
                          <div className="trunc-text" style={{ maxWidth: "80px" }} title={row.location || ""}>
                            {row.location || "—"}
                          </div>
                        </td>
                        <td>
                          <div className="trunc-text" style={{ maxWidth: "80px" }} title={row.contact_person || ""}>
                            {row.contact_person || "—"}
                          </div>
                        </td>
                        <td>
                          <div className="trunc-text" style={{ maxWidth: "90px" }} title={row.contact_number || ""}>
                            {row.contact_number || "—"}
                          </div>
                        </td>
                        <td className="text-nowrap">{displayDate(row.installed_date)}</td>
                        <td>
                          {row.duration_years
                            ? `${row.duration_years} Yr${row.duration_years > 1 ? "s" : ""}`
                            : row.duration_days
                            ? `${row.duration_days} days`
                            : "—"}
                        </td>
                        <td className="text-nowrap">{displayDate(row.ending_on)}</td>
                        <td className="text-center">{renderDaysDiff(row.ending_on)}</td>
                        <td className="text-nowrap">{displayDate(row.to_renew)}</td>
                        <td>{row.qty ?? "—"}</td>
                        <td>{row.total_systems ?? "—"}</td>
                        <td>{money(row.rate)}</td>
                        <td className="fw-semibold">{money(row.rate_details)}</td>
                        <td>
                          {row.status ? (
                            <span className={`badge ${row.status === "Active" ? "bg-success" : row.status === "Expired" ? "bg-danger" : "bg-secondary"}`}>
                              {row.status}
                            </span>
                          ) : "—"}
                        </td>
                        <td>
                          {row.invoice_number ? (
                            <span className="fw-semibold">{row.invoice_number}</span>
                          ) : (
                            <span className="badge bg-secondary">No</span>
                          )}
                        </td>
                        <td style={{ maxWidth: "180px" }} title={row.license_details || ""}>
                          {row.license_details || "—"}
                        </td>
                        <td className="text-nowrap">
                          {allowEdit && (
                          <button className="btn btn-sm btn-outline-warning me-1" onClick={() => openEdit(row)}>
                            <i className="fas fa-pen"></i>
                          </button>
                          )}
                          {allowEdit && isSuperAdmin && (
                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(row.id)}>
                              <i className="fas fa-trash-alt"></i>
                            </button>
                          )}
                          {!allowEdit && <span className="text-muted">View only</span>}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="21" className="text-center text-muted py-4">
                        No child keys found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal show d-block child-modal" style={{ backgroundColor: "rgba(15,23,42,0.45)" }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content border-0 shadow">
              <form onSubmit={handleSubmit} className="child-form">
                <div className="modal-header border-0 pb-0">
                  <div>
                    <h5 className="modal-title mb-1">{editing ? "Edit Child Key" : "Add Child Key"}</h5>
                    <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                      Child key belongs to a Sub Key. Rate details = Rate × Total systems.
                    </p>
                  </div>
                  <button type="button" className="btn-close" onClick={resetModal}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Sub Key <span className="text-danger">*</span>
                    </label>
                    <Select
                      options={modalSubOptions}
                      value={
                        modalSubOptions.find(
                          (o) => o.value === String(form.ms_sub_key_id)
                        ) || null
                      }
                      onChange={(opt) =>
                        handleChange({
                          target: {
                            name: "ms_sub_key_id",
                            value: opt?.value || "",
                          },
                        })
                      }
                      placeholder="Search / select sub key..."
                      isSearchable
                      isDisabled={!!editing}
                      filterOption={quotaFilterOption}
                      components={{ Option: QuotaOption, SingleValue: QuotaSingleValue }}
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                    />
                    {form.ms_sub_key_id && !editing && (
                      <div className="quota-bar mt-2">
                        <span className="quota-pill used">
                          Used: <b>{usedChildCount}</b> / {MAX_CHILD_KEYS}
                        </span>
                        <span className={`quota-pill ${leftChildCount === 0 ? "full" : "left"}`}>
                          Left: <b>{leftChildCount}</b>
                        </span>
                      </div>
                    )}
                    {form.ms_sub_key_id && !editing && leftChildCount === 0 && (
                      <div className="text-danger mt-1" style={{ fontSize: "13px" }}>
                        This sub key already has {MAX_CHILD_KEYS} child keys. No slots left.
                      </div>
                    )}
                  </div>

                  <div className="section-box">
                    <div className="section-title">Customer</div>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label">Company Name</label>
                        <input className="form-control" name="company_name" value={form.company_name} onChange={handleChange} />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Place / Location of Customer</label>
                        <input className="form-control" name="location" value={form.location} onChange={handleChange} />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Contact Person</label>
                        <input className="form-control" name="contact_person" value={form.contact_person} onChange={handleChange} />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Contact Number</label>
                        <input className="form-control" name="contact_number" value={form.contact_number} onChange={handleChange} />
                      </div>
                    </div>
                  </div>

                  <div className="section-box mt-3">
                    <div className="section-title">Billing period</div>
                    <div className="row g-3">
                      <div className="col-md-3">
                        <label className="form-label">Installed Date</label>
                        <input
                          type="date"
                          className="form-control"
                          name="installed_date"
                          value={form.installed_date}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Duration</label>
                        <select
                          className="form-select"
                          name="duration_years"
                          value={form.duration_years}
                          onChange={handleChange}
                        >
                          <option value="">Select years</option>
                          {YEAR_OPTIONS.map((y) => (
                            <option key={y} value={y}>
                              {y} Year{y > 1 ? "s" : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Ending On</label>
                        <input
                          type="date"
                          className="form-control bg-light"
                          name="ending_on"
                          value={form.ending_on}
                          onChange={handleChange}
                          readOnly={!!form.duration_years}
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">To Renew</label>
                        <input
                          type="date"
                          className="form-control"
                          name="to_renew"
                          value={form.to_renew}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <small className="text-muted d-block mt-2">
                      Dates copy from the sub key. Change duration to recalculate ending / renew.
                    </small>
                  </div>

                  <div className="section-box mt-3">
                    <div className="section-title">License & billing</div>
                    <div className="row g-3">
                      <div className="col-md-3">
                        <label className="form-label">Duration (Days)</label>
                        <input type="number" min="0" className="form-control" name="duration_days" value={form.duration_days} onChange={handleChange} />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Qty</label>
                        <input type="number" min="0" className="form-control" name="qty" value={form.qty} onChange={handleChange} />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Total No of Systems</label>
                        <input type="number" min="0" className="form-control" name="total_systems" value={form.total_systems} onChange={handleChange} />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Rate</label>
                        <input type="number" min="0" step="0.01" className="form-control" name="rate" value={form.rate} onChange={handleChange} />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Rate Details</label>
                        <input className="form-control bg-light fw-semibold" value={money(rateDetails)} readOnly />
                        <small className="text-muted">Auto: Rate × Systems</small>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Status</label>
                        <select className="form-select" name="status" value={form.status} onChange={handleChange}>
                          {STATUSES.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">License Number & details</label>
                        <input className="form-control" name="license_details" value={form.license_details} onChange={handleChange} />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Invoice Number</label>
                        <input
                          className="form-control"
                          name="invoice_number"
                          value={form.invoice_number}
                          onChange={handleChange}
                          placeholder="Enter invoice number"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer border-0">
                  <button type="button" className="btn btn-light border" onClick={resetModal}>Cancel</button>
                  <button
                    type="submit"
                    className="btn btn-primary px-4"
                    disabled={isLoading || (!editing && form.ms_sub_key_id && leftChildCount === 0)}
                  >
                    {isLoading ? "Saving..." : editing ? "Update" : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .child-table-wrap { border: 1px solid #e2e8f0; border-radius: 8px; overflow: auto; max-height: 70vh; }
        .child-table thead th { position: sticky; top: 0; background: #0f766e; color: #fff; font-size: 12px; white-space: nowrap; z-index: 1; }
        .child-table td { font-size: 13px; }
        .child-table .trunc-text {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          display: block;
          cursor: default;
        }
        .child-table tbody tr.group-row > td {
          background-color: var(--row-bg) !important;
        }
        .child-table tbody tr.group-row:hover > td { filter: brightness(0.97); }
        .child-modal .modal-dialog { max-width: 1100px; margin: 16px auto; height: calc(100vh - 32px); max-height: calc(100vh - 32px); }
        .child-modal .modal-content { height: 100%; display: flex; flex-direction: column; }
        .child-form { display: flex; flex-direction: column; height: 100%; min-height: 0; }
        .child-modal .modal-body { flex: 1 1 auto; overflow-y: auto; min-height: 0; }
        .child-modal .form-label { font-size: 12px; font-weight: 600; }
        .section-box { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px 16px; }
        .quota-bar { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
        .quota-pill { display: inline-flex; align-items: center; gap: 4px; border-radius: 999px; padding: 4px 12px; font-size: 12px; font-weight: 600; }
        .quota-pill.used { background: #dbeafe; color: #1d4ed8; }
        .quota-pill.left { background: #dcfce7; color: #166534; }
        .quota-pill.full { background: #fee2e2; color: #b91c1c; }
        .section-title { font-size: 11px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: #64748b; margin-bottom: 10px; }
        .quota-opt-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          width: 100%;
          cursor: help;
        }
        .quota-opt-row.compact { gap: 8px; }
        .quota-opt-label {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          min-width: 0;
          cursor: help;
        }
        .quota-opt-badges {
          display: inline-flex;
          flex-shrink: 0;
          gap: 4px;
        }
        .opt-badge {
          display: inline-flex;
          align-items: center;
          border-radius: 999px;
          padding: 1px 8px;
          font-size: 11px;
          font-weight: 700;
          line-height: 1.4;
          white-space: nowrap;
        }
        .opt-badge.used { background: #dbeafe; color: #1d4ed8; }
        .opt-badge.left { background: #dcfce7; color: #166534; }
        .opt-badge.full { background: #fee2e2; color: #b91c1c; }
        .quota-opt-badges.dark .opt-badge.used { background: #bfdbfe; }
        .quota-opt-badges.dark .opt-badge.left { background: #bbf7d0; }
        .quota-opt-badges.dark .opt-badge.full { background: #fecaca; }
      `}</style>
    </div>
  );
}
